<template>
  <v-card tile>
    <v-card-title>{{ isNew ? "Nuevo Envío" : "Editar Envío" }}</v-card-title>
    <v-card-text>
      <v-select
        v-model="form.company"
        :items="companies"
        item-text="name"
        item-value="_id"
        label="Empresa"
        :loading="loadingLookups"
        :disabled="saving || loadingLookups"
      />

      <v-select
        v-model="form.purpose"
        :items="purposeOptions"
        item-text="text"
        item-value="value"
        label="Propósito"
        :disabled="saving"
      />

      <!-- Filtrada por proposito: el backend rechaza una plantilla que no sea
           del mismo proposito que el Envio, y ofrecer solo las que calzan
           evita ese error. -->
      <v-select
        v-model="form.template"
        :items="filteredTemplates"
        item-text="label"
        item-value="_id"
        label="Plantilla"
        :loading="loadingLookups"
        :disabled="saving || loadingLookups || !form.purpose"
        :no-data-text="noTemplatesText"
        :hint="templatesHint"
        persistent-hint
      >
        <!-- El texto se pinta explicitamente desde `name` en los dos lugares
             -la lista y el valor elegido- en vez de dejarlo librado a que
             Vuetify resuelva `item-text`. Es el NOMBRE de la plantilla lo que
             tiene que verse; ningun otro campo del documento (header,
             bodyText) puede llegar a la pantalla por este camino. -->
        <template v-slot:item="{ item }">{{ templateLabel(item) }}</template>
        <template v-slot:selection="{ item }">{{ templateLabel(item) }}</template>
      </v-select>

      <v-text-field
        v-model="form.subject"
        label="Asunto"
        hint="Obligatorio. Acepta los mismos marcadores que el texto de la plantilla."
        persistent-hint
        :disabled="saving"
      />
      <div class="body-2 grey--text mt-2 mb-2">
        Marcadores disponibles: {{ availableTokens.join(", ") }}.
        $DIA_HABIL_N es el primer día hábil desde el día N del mes del envío
        (por ejemplo $DIA_HABIL_20: si el 20 cae domingo, pone 21).
      </div>

      <v-switch v-model="form.isActive" label="Activo" :disabled="saving" />

      <v-divider class="my-3" />

      <template v-if="form.purpose === PURPOSE_DAILY">
        <div class="body-2 grey--text mb-2">
          El envío diario corre todos los días hábiles.
        </div>
        <!-- La hora va junto al dia: es la otra mitad de "cuando sale". -->
        <v-select
          v-model="form.sendHour"
          :items="hourOptions"
          item-text="text"
          item-value="value"
          label="Hora de envío"
          :disabled="saving"
        />
        <div class="body-2 grey--text mb-2">
          Sale en la primera corrida a partir de esa hora, en punto. Si el sistema
          estuvo caído a esa hora, sale apenas vuelve, el mismo día, y una sola vez.
        </div>
      </template>

      <template v-else-if="form.purpose === PURPOSE_MONTHLY">
        <div class="body-2 mt-2 mb-1">Día de envío</div>
        <schedule-selector v-model="form.monthly.schedule" day-label="Día del envío mensual" />
        <!-- La hora va junto al dia: es la otra mitad de "cuando sale". -->
        <v-select
          v-model="form.sendHour"
          :items="hourOptions"
          item-text="text"
          item-value="value"
          label="Hora de envío"
          :disabled="saving"
        />
        <div class="body-2 grey--text mb-2">
          Sale en la primera corrida a partir de esa hora, en punto. Si el sistema
          estuvo caído a esa hora, sale apenas vuelve, el mismo día, y una sola vez.
        </div>
      </template>

      <template v-else-if="form.purpose === PURPOSE_REMINDER">
        <day-list v-model="form.reminder.days" label="Días de recordatorio" />
        <v-checkbox
          v-model="form.reminder.weekdaysOnly"
          label="Sólo de lunes a viernes (si un día cae en fin de semana, sale el lunes)"
          hide-details
          class="mt-0 mb-2"
          :disabled="saving"
        />
        <div class="body-2 grey--text mb-2">
          Cada día lleva su propia hora. Sin hora, ese día sale en la corrida
          diaria. Sale en la primera corrida a partir de esa hora, en punto.
        </div>
      </template>

      <template v-else-if="form.purpose === PURPOSE_TAG">
        <tag-select
          v-model="form.tag.tags"
          :items="tags"
          label="Etiquetas"
          :loading="loadingLookups"
          :disabled="saving"
        />
        <div class="body-2 grey--text mb-2">
          Entran todas las obligaciones con estas etiquetas, estén vencidas o no.
        </div>

        <!-- En vez de cada contratista con sus obligaciones, una lista con
             vinetas de las contratistas incluidas. -->
        <v-checkbox
          v-model="form.tag.contractorNamesOnly"
          label="Solo mostrar nombre de contratistas"
          hint="El correo lista con viñetas las contratistas incluidas, sin el detalle de obligaciones."
          persistent-hint
          class="mt-0 mb-2"
          :disabled="saving"
        />

        <v-radio-group v-model="form.tag.contractorOrder" label="Orden de las contratistas" :disabled="saving" class="mt-2">
          <v-radio label="Órden alfabético" value="alphabetical" />
          <v-radio label="Primer obligación vencida" value="firstExpired" />
          <v-radio label="Última obligación vencida" value="lastExpired" />
        </v-radio-group>

        <!-- Adjuntos: los archivos cargados a esas obligaciones, del mes
             elegido. Tope de 20 MB por correo; lo que no entra queda anotado
             en el reporte de la corrida. -->
        <v-switch v-model="form.tag.attachFiles" label="Enviar archivos adjuntos" :disabled="saving" />
        <v-row v-if="form.tag.attachFiles" align="center" no-gutters>
          <v-col cols="12" md="5">
            <v-checkbox
              v-model="form.tag.attachOnlyPeriod"
              label="Solo enviar los del"
              :disabled="saving"
              class="mt-0"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="form.tag.attachPeriod"
              :items="attachPeriodOptions"
              item-text="text"
              item-value="value"
              dense
              :disabled="saving || !form.tag.attachOnlyPeriod"
            />
          </v-col>
        </v-row>
        <v-checkbox
          v-if="form.tag.attachFiles"
          v-model="form.tag.attachOriginalNames"
          label="Enviar archivos con el nombre original"
          :disabled="saving"
          class="mt-0"
          hide-details
        />
        <div v-if="form.tag.attachFiles" class="body-2 grey--text mb-2">
          Sin "Solo enviar los del" van todos los archivos de cada obligación. Tope de
          20 MB por correo: lo que no entra no se adjunta y queda anotado en el
          reporte de la corrida.
        </div>

        <div class="body-2 mt-2 mb-1">Día de envío</div>
        <schedule-selector
          v-model="form.tag.schedule"
          :allowed="['dayOfMonth', 'lastDay', 'lastBusinessDay']"
        />
        <!-- La hora va junto al dia: es la otra mitad de "cuando sale". -->
        <v-select
          v-model="form.sendHour"
          :items="hourOptions"
          item-text="text"
          item-value="value"
          label="Hora de envío"
          :disabled="saving"
        />
        <div class="body-2 grey--text mb-2">
          Sale en la primera corrida a partir de esa hora, en punto. Si el sistema
          estuvo caído a esa hora, sale apenas vuelve, el mismo día, y una sola vez.
        </div>

        <email-list v-model="form.tag.recipientEmails" label="Direcciones que reciben este envío" />
        <email-list v-model="form.tag.ccEmails" label="Con copia (CC)" />
        <email-list v-model="form.tag.bccEmails" label="Con copia oculta (CCO)" />
      </template>

      <template v-else-if="form.purpose === PURPOSE_MONTH_END_REPORT">
        <div class="body-2 mt-2 mb-1">Día de envío</div>
        <schedule-selector v-model="form.monthEndReport.schedule" />
        <!-- La hora va junto al dia: es la otra mitad de "cuando sale". -->
        <v-select
          v-model="form.sendHour"
          :items="hourOptions"
          item-text="text"
          item-value="value"
          label="Hora de envío"
          :disabled="saving"
        />
        <div class="body-2 grey--text mb-2">
          Sale en la primera corrida a partir de esa hora, en punto. Si el sistema
          estuvo caído a esa hora, sale apenas vuelve, el mismo día, y una sola vez.
        </div>

        <tag-select
          v-model="form.monthEndReport.tags"
          :items="tags"
          label="Etiquetas a considerar"
          :disabled="saving"
        />
        <div class="body-2 grey--text mb-2">
          Las obligaciones con estas etiquetas se informan una por una. Sin etiquetas
          se informan todas.
        </div>
        <v-text-field
          v-model="form.monthEndReport.otherObligationsText"
          label="Texto para otras obligaciones de contratista vencidas o no presentadas"
          :disabled="saving"
        />
        <v-text-field
          v-model="form.monthEndReport.employeeOwesText"
          label="Texto para empleados con más de una obligación de nómina pendiente"
          hint="Va después del nombre del empleado."
          persistent-hint
          :disabled="saving"
        />

        <v-radio-group v-model="form.monthEndReport.contractorOrder" label="Orden de las contratistas" :disabled="saving" class="mt-2" hide-details>
          <v-radio label="Órden alfabético" value="alphabetical" />
          <v-radio label="Primer obligación vencida" value="firstExpired" />
          <v-radio label="Última obligación vencida" value="lastExpired" />
        </v-radio-group>
        <div class="body-2 grey--text mt-1 mb-2">
          Los subcontratistas siempre van debajo de su contratista principal.
        </div>

        <email-list
          v-model="form.monthEndReport.recipientEmails"
          label="Direcciones que reciben este reporte"
        />
        <email-list v-model="form.monthEndReport.ccEmails" label="Con copia (CC)" />
        <email-list v-model="form.monthEndReport.bccEmails" label="Con copia oculta (CCO)" />

        <v-switch
          v-model="form.monthEndReport.usePreviousMonth"
          label="Enviar el mes anterior"
          :disabled="saving"
        />
        <v-switch
          v-model="form.monthEndReport.attachPdf"
          label="Adjuntar planilla PDF de aptitud"
          hint="Status anterior y actual de cada contratista, con sus subcontratistas, servicio, contacto y observaciones."
          persistent-hint
          :disabled="saving"
        />
        <!-- Sin la extension: el .pdf lo agrega el sistema. -->
        <v-text-field
          v-if="form.monthEndReport.attachPdf"
          v-model="form.monthEndReport.pdfFileName"
          class="mt-4"
          label="Nombre del archivo PDF"
          suffix=".pdf"
          placeholder="Informe <empresa> - <mes y año>"
          hint="Acepta marcadores, por ejemplo: Informe $AÑO - $EMPRESA - $MES_ANTERIOR. Vacío usa el nombre por defecto."
          persistent-hint
          :disabled="saving"
        />
        <v-radio-group
          v-if="form.monthEndReport.attachPdf"
          v-model="form.monthEndReport.columnGrouping"
          label="Agrupar columnas"
          :disabled="saving"
          class="mt-4"
          hide-details
        >
          <v-radio label="Sin agrupar" value="none" />
          <v-radio label="Agrupar Razón social y Servicio prestado" value="nameAndService" />
          <v-radio label="Agrupar Servicio prestado y Contacto" value="serviceAndContact" />
        </v-radio-group>
      </template>

      <v-divider class="my-3" />

      <!-- Notas internas del Envio: no salen en ningun correo. -->
      <v-textarea
        v-model="form.notes"
        label="Notas"
        hint="Para uso interno; no se envían en el correo."
        persistent-hint
        rows="3"
        auto-grow
        outlined
        dense
        :disabled="saving"
      />

      <!-- CCO general: va en todos los correos de este Envio, sea cual sea el
           proposito. Etiquetas y fin de mes ademas tienen su propia CC/CCO. -->
      <email-list v-model="form.bccEmails" label="CCO en todos los correos de este Envío" />

      <!-- Abierto solo si ya tiene algo configurado. -->
      <v-expansion-panels v-model="scheduledTestPanel" flat class="mt-2 scheduled-test">
        <v-expansion-panel>
          <v-expansion-panel-header class="px-0">Prueba programada</v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="body-2 grey--text mb-2">
              Se manda todos los meses, el día y la hora elegidos, únicamente a este
              correo, aunque el Envío esté inactivo. No llega a nadie más.
            </div>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.scheduledTest.toEmail" label="Correo de destino" :disabled="saving" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.scheduledTest.subject" label="Asunto" :disabled="saving" />
              </v-col>
              <v-col cols="12">
                <div class="body-2 mt-2 mb-1">Día de envío</div>
                <schedule-selector v-model="form.scheduledTest.schedule" />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.scheduledTest.hour"
                  :items="hourOptions"
                  label="Hora"
                  clearable
                  :disabled="saving"
                />
              </v-col>
            </v-row>
            <div v-if="form.scheduledTest.lastResult" class="body-2">
              Último resultado: {{ form.scheduledTest.lastResult }}
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-alert v-if="errorMessage" type="error" dense text class="mt-4">{{ errorMessage }}</v-alert>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn :disabled="saving" @click="cancel">Cancelar</v-btn>
      <v-btn
        class="primary"
        :loading="saving"
        :disabled="!form.company || !form.purpose || !form.template || saving"
        @click="save"
      >Guardar</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
"use strict";

import emailDeliveryService from "@/services/EmailDeliveryService";
import emailTemplateService from "@/services/EmailTemplateService";
import companyService from "@/services/CompanyService";
import tagsService from "@/services/TagsService";
import scheduleSelector from "@/components/views/companies/schedule-selector.component.vue";
import emailList from "@/components/views/companies/email-list.component.vue";
import dayList from "@/components/views/emails/day-list.component.vue";
import tagSelect from "@/components/common/tag-select.component.vue";
import { HOUR_OPTIONS } from "@/utils/send-hour";
import plainText from "@/filters/plain-text";

// Los cinco propositos de constants.EMAIL_TYPES, duplicados aca como en
// list-email-deliveries.view.vue: el frontend no importa el modulo de
// constantes del backend.
const PURPOSE_DAILY = "daily";
const PURPOSE_MONTHLY = "monthly";
const PURPOSE_REMINDER = "reminder";
const PURPOSE_TAG = "tag";
const PURPOSE_MONTH_END_REPORT = "monthEndReport";

// Los marcadores del asunto. $HOY es la fecha de la corrida; $FECHA, la fecha
// limite que se le pide a la contratista.
// $PLURAL_CONTACTOS es la "s" que concuerda con $CONTACTO cuando enumera mas
// de un nombre ("Estimado$PLURAL_CONTACTOS $CONTACTO").
const CONTRACTOR_TOKENS = ["$NOMBRE", "$APELLIDO", "$CONTACTO", "$PLURAL_CONTACTOS", "$CONTRATISTA"];
const SHARED_TOKENS = ["$EMPRESA", "$NOMBRE_CONTACTO", "$APELLIDO_CONTACTO", "$CORREO", "$FECHA", "$HOY", "$MES", "$MES_NUMERO", "$MES_ANTERIOR", "$AÑO", "$DIA_HABIL_N"];

// Que bloque del formulario le corresponde a cada proposito, igual que
// PURPOSE_CONFIG_FIELD en email-delivery-validation.js: el diario no lleva
// ninguno.
const PURPOSE_CONFIG_FIELD = {
  [PURPOSE_DAILY]: null,
  [PURPOSE_MONTHLY]: "monthly",
  [PURPOSE_REMINDER]: "reminder",
  [PURPOSE_TAG]: "tag",
  [PURPOSE_MONTH_END_REPORT]: "monthEndReport"
};

// Mismo criterio que email-delivery-validation.js (isScheduleConfigured): un
// Schedule cuenta como configurado cuando tiene dayType, y si ese dayType es
// dayOfMonth, ademas un dayOfMonth.
const isScheduleConfigured = schedule => {
  if (!schedule || !schedule.dayType) return false;
  if (schedule.dayType === "dayOfMonth") return !!schedule.dayOfMonth;
  return true;
};

const hasEmails = emails => Array.isArray(emails) && emails.some(email => email && email.trim());

export default {
  name: "edit-email-delivery",
  components: {
    "schedule-selector": scheduleSelector,
    "email-list": emailList,
    "day-list": dayList,
    "tag-select": tagSelect
  },
  props: {
    delivery: { type: Object, required: true },
    purposeOptions: { type: Array, required: true }
  },
  data() {
    return {
      // Igual que edit-email-template.view.vue: el formulario se arma campo
      // por campo -y no con un Object.assign ciego- porque delivery.company y
      // delivery.template pueden llegar como el objeto poblado (desde el
      // listado) o como un id crudo (recien guardado).
      form: {
        company: (this.delivery.company && this.delivery.company._id) || this.delivery.company || "",
        purpose: this.delivery.purpose || "",
        template: (this.delivery.template && this.delivery.template._id) || this.delivery.template || "",
        subject: this.delivery.subject || "",
        // null = "en la corrida diaria". Se compara contra undefined y no con
        // `||`, porque la hora 0 es valida.
        sendHour: this.delivery.sendHour === undefined ? null : this.delivery.sendHour,
        notes: this.delivery.notes || "",
        bccEmails: this.delivery.bccEmails || [],
        isActive: this.delivery.isActive !== undefined ? this.delivery.isActive : true,
        monthly: Object.assign({ schedule: null }, this.delivery.monthly || {}),
        scheduledTest: Object.assign({ toEmail: "", subject: "", schedule: null, hour: null },
          this.delivery.scheduledTest || {}),
        reminder: Object.assign({ days: [], weekdaysOnly: true }, this.delivery.reminder || {}),
        tag: Object.assign(
          { tags: [], schedule: null, recipientEmails: [], ccEmails: [], bccEmails: [],
            attachFiles: false, attachOnlyPeriod: true, attachPeriod: "current",
            attachOriginalNames: true, contractorOrder: "alphabetical", contractorNamesOnly: false },
          this.delivery.tag || {}
        ),
        monthEndReport: Object.assign(
          { tags: [], columnGrouping: "none", contractorOrder: "alphabetical", otherObligationsText: "Adeuda documentación solicitada por correo.", employeeOwesText: "figura en la nómina de personal autorizado y adeuda documentación.", schedule: null, recipientEmails: [], ccEmails: [], bccEmails: [], usePreviousMonth: false, attachPdf: false, pdfFileName: "" },
          this.delivery.monthEndReport || {}
        )
      },
      // La plantilla que el Envio ya tenia asignada, tal como llego -poblada,
      // con nombre y estado-. Se guarda aparte del catalogo (`templates`,
      // abajo) porque ese catalogo puede dejar de incluirla (se desactivo o
      // se borro despues de asignarse) y sin esta copia no habria forma de
      // seguir mostrandola en el selector. Ver comentario de
      // `filteredTemplates`.
      originalTemplate: (this.delivery.template && this.delivery.template._id) ? this.delivery.template : null,
      companies: [],
      templates: [],
      tags: [],
      hourOptions: HOUR_OPTIONS,
      scheduledTestPanel: this.delivery.scheduledTest && this.delivery.scheduledTest.toEmail ? 0 : undefined,
      attachPeriodOptions: [
        { value: "current", text: "presente mes" },
        { value: "previous", text: "mes anterior" },
        { value: "next", text: "mes siguiente" }
      ],
      loadingLookups: false,
      saving: false,
      errorMessage: null,
      PURPOSE_DAILY,
      PURPOSE_MONTHLY,
      PURPOSE_REMINDER,
      PURPOSE_TAG,
      PURPOSE_MONTH_END_REPORT
    };
  },
  computed: {
    isNew() {
      return !this.delivery._id;
    },
    // Los marcadores que el asunto puede usar, segun el proposito: etiquetas y
    // fin de mes van a direcciones sueltas y no tienen contratista, asi que
    // los cuatro que dependen de una se ofrecen solo en los otros tres.
    // Mismo criterio que availableTokens en edit-email-template.view.vue.
    availableTokens() {
      const goesToLooseAddresses =
        this.form.purpose === PURPOSE_TAG || this.form.purpose === PURPOSE_MONTH_END_REPORT;
      return goesToLooseAddresses ? SHARED_TOKENS : CONTRACTOR_TOKENS.concat(SHARED_TOKENS);
    },
    // Solo las plantillas del proposito elegido, activas: email-delivery-
    // validation.js (assertValid, en create() y en edit()) rechaza guardar
    // con una plantilla borrada o inactiva, asi que ofrecerlas dejaria
    // elegir algo que el backend SIEMPRE va a rechazar.
    //
    // Caso borde -elegido de las dos formas que planteo el brief, comentado
    // aca a proposito-: un Envio ya guardado puede apuntar a una plantilla
    // que se desactivo (o se borro) DESPUES de asignarse. Filtrar sin mas
    // dejaria el selector vacio de esa opcion al abrir el Envio para editar,
    // y guardar sin tocar el campo la borraria del documento -exactamente el
    // tipo de perdida silenciosa que este plan viene evitando (ver
    // email-delivery.domain.js, comentario de `edit()`-. En vez de eso, esa
    // plantilla se sigue ofreciendo, marcada como inactiva/borrada, para que
    // abrir el Envio no le cambie nada. Si el administrador la deja asi y
    // aprieta Guardar, el propio mensaje del backend ("La plantilla asignada
    // esta inactiva"/"fue borrada") le explica por que tiene que elegir otra
    // - no hace falta duplicar esa validacion aca ademas.
    // Un selector vacio no dice por que esta vacio. Las tres razones posibles
    // son distintas y el administrador no puede adivinar cual le toco: que no
    // haya ninguna plantilla de ese proposito, que las que hay esten
    // desactivadas -el backend rechaza guardar un Envio con una plantilla
    // inactiva, asi que no se ofrecen-, o que todavia no eligio proposito.
    inactiveTemplatesForPurpose() {
      if (!this.form.purpose) return 0;
      return this.templates.filter(
        t => t.purpose === this.form.purpose && !t.isActive).length;
    },
    noTemplatesText() {
      if (!this.form.purpose) return "Elegí primero un propósito";
      if (this.inactiveTemplatesForPurpose > 0) {
        return "No hay plantillas activas para este propósito";
      }
      return "No hay plantillas de este propósito";
    },
    templatesHint() {
      if (!this.form.purpose || this.inactiveTemplatesForPurpose === 0) return "";
      const count = this.inactiveTemplatesForPurpose;
      return count === 1
        ? "Hay 1 plantilla de este propósito desactivada, por eso no aparece."
        : `Hay ${count} plantillas de este propósito desactivadas, por eso no aparecen.`;
    },
    // `label` y no `name` a secas: el nombre es una etiqueta, y si el dato
    // guardado trae marcado -paso con plantillas cargadas antes de que esta
    // pantalla existiera- mostrarlo crudo pinta "<p>Prueba</p>" en el selector
    // y no se puede elegir con confianza. plainText lo deja legible sin tocar
    // el dato.
    filteredTemplates() {
      // Los items del selector llevan SOLO lo que el selector necesita: el id
      // para el v-model y la etiqueta para mostrar. No se pasa el documento
      // entero a proposito -antes se hacia con Object.assign-, asi el objeto
      // que recibe Vuetify ni siquiera contiene `header` ni `bodyText`, y no
      // hay ningun camino por el que el contenido de la plantilla pueda
      // terminar pintado en la lista.
      const withLabel = list => list.map(t => ({ _id: t._id, label: plainText(t.name) }));
      const active = this.templates.filter(t => t.purpose === this.form.purpose && t.isActive);
      if (!this.form.template || active.some(t => t._id === this.form.template)) {
        return withLabel(active);
      }
      const stale = (this.originalTemplate && this.originalTemplate._id === this.form.template)
        ? this.originalTemplate
        : this.templates.find(t => t._id === this.form.template);
      if (!stale || stale.purpose !== this.form.purpose) {
        return withLabel(active);
      }
      const state = stale.isDeleted ? "borrada" : "inactiva";
      return withLabel(active).concat(
        { _id: stale._id, label: `${plainText(stale.name)} (${state})` });
    }
  },
  watch: {
    "form.purpose"() {
      // La plantilla elegida deja de ser valida al cambiar de proposito: el
      // backend exige que coincidan. Limpiar es mejor que dejar una
      // seleccion que va a ser rechazada al guardar.
      this.form.template = null;
    }
  },
  created() {
    // En created() y no en mounted(), mismo motivo que edit-company.view.vue:
    // el created() de los hijos schedule-selector corre ANTES que el
    // mounted() de este dialogo, y esos hijos normalizan y emiten un default
    // propio si reciben un valor vacio. Si esta guarda corriera despues, el
    // default del hijo ganaria y pisaria el criterio de esta pantalla.
    if (!this.form.monthly.schedule) {
      this.form.monthly.schedule = { dayType: "firstBusinessDay", dayOfMonth: null };
    }
    if (!this.form.tag.schedule) {
      this.form.tag.schedule = { dayType: "dayOfMonth", dayOfMonth: 1 };
    }
    if (!this.form.monthEndReport.schedule) {
      this.form.monthEndReport.schedule = { dayType: "firstBusinessDay", dayOfMonth: null };
    }
  },
  async mounted() {
    await this.loadLookups();
    // Las etiquetas llegan como ids o como objetos segun si el backend
    // populo, igual que tag-mailing-dialog.component.vue.
    this.form.tag.tags = (this.form.tag.tags || []).map(t => (typeof t === "string" ? t : t._id));
    this.form.monthEndReport.tags = (this.form.monthEndReport.tags || []).map(t => (typeof t === "string" ? t : t._id));
  },
  methods: {
    // Siempre el nombre de la plantilla, pasado por plainText para que un
    // nombre guardado con marcado se lea como texto y no como codigo.
    templateLabel(item) {
      if (!item) return "";
      return plainText(item.label || item.name || "");
    },
    messageOf(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    async loadLookups() {
      this.errorMessage = null;
      this.loadingLookups = true;
      try {
        const [companies, templates, tags] = await Promise.all([
          companyService.getCompanies(true),
          emailTemplateService.list(),
          tagsService.list()
        ]);
        this.companies = companies;
        this.templates = templates;
        this.tags = tags;
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudieron cargar los datos del formulario");
      } finally {
        this.loadingLookups = false;
      }
    },
    // Que el bloque del proposito elegido este completo. Son las mismas
    // reglas que domains/email-delivery-validation.js: la del formulario
    // existe para que el error llegue antes, no para reemplazar la del
    // backend.
    validateConfig() {
      switch (this.form.purpose) {
        case PURPOSE_MONTHLY:
          if (!isScheduleConfigured(this.form.monthly.schedule)) {
            return "El Envío mensual necesita un día configurado";
          }
          return null;

        case PURPOSE_REMINDER:
          if (!this.form.reminder.days.length) {
            return "El Envío de recordatorio necesita al menos un día";
          }
          return null;

        case PURPOSE_TAG:
          if (!this.form.tag.tags.length) {
            return "El Envío de etiquetas necesita al menos una etiqueta";
          }
          if (!hasEmails(this.form.tag.recipientEmails)) {
            return "El Envío de etiquetas necesita al menos una dirección de destino";
          }
          return null;

        case PURPOSE_MONTH_END_REPORT:
          if (!isScheduleConfigured(this.form.monthEndReport.schedule)) {
            return "El Envío de fin de mes necesita un día configurado";
          }
          if (!hasEmails(this.form.monthEndReport.recipientEmails)) {
            return "El Envío de fin de mes necesita al menos una dirección de destino";
          }
          return null;

        case PURPOSE_DAILY:
        default:
          return null;
      }
    },
    validate() {
      if (!this.form.company) return "La empresa es obligatoria";
      if (!this.form.purpose) return "El propósito es obligatorio";
      if (!this.form.template) return "La plantilla es obligatoria";
      if (!this.form.subject || !this.form.subject.trim()) return "El asunto es obligatorio";
      return this.validateConfig();
    },
    // Solo el bloque del proposito elegido viaja: los otros cuatro vacios
    // ensuciarian el documento con subdocumentos que nadie lee.
    buildPayload() {
      const payload = {
        company: this.form.company,
        purpose: this.form.purpose,
        template: this.form.template,
        subject: this.form.subject,
        sendHour: this.form.sendHour,
        notes: this.form.notes,
        bccEmails: this.form.bccEmails,
        isActive: this.form.isActive,
        scheduledTest: this.form.scheduledTest
      };
      const field = PURPOSE_CONFIG_FIELD[this.form.purpose];
      if (field) {
        payload[field] = this.form[field];
      }
      return payload;
    },
    cancel() {
      this.$emit("cancel");
    },
    async save() {
      if (!this.form.company || !this.form.purpose || !this.form.template || this.saving) return;
      const error = this.validate();
      if (error) {
        this.errorMessage = error;
        return;
      }
      this.errorMessage = null;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        const saved = this.isNew
          ? await emailDeliveryService.create(payload)
          : await emailDeliveryService.edit(this.delivery._id, payload);
        this.$emit("saved", saved);
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo guardar el Envío");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
